import { Device } from './Device'

export const createScriptTag = (src: string): HTMLScriptElement => {
  const tag = document.createElement('script')
  tag.setAttribute('src', src)
  tag.setAttribute('type', 'text/javascript')
  return tag
}

export function insertScript(script: HTMLScriptElement, callback?: Function) {
  const firstScript = document.getElementsByTagName(
    'script'
  )[0] as HTMLScriptElement
  const parentNode = firstScript.parentNode as HTMLElement
  const errorMessage = `Error loading asset ${script.getAttribute('src')}`
  parentNode.insertBefore(script, firstScript)
  script.addEventListener('load', () => (callback ? callback() : null))
  script.addEventListener('error', () =>
    callback ? callback(errorMessage) : null
  )
}

export function writeCss(styles: string): void {
  const head = document.head || document.getElementsByTagName('head')[0]
  const styleEl = document.createElement('style')
  styleEl.type = 'text/css'
  if (styleEl.styleSheet) {
    styleEl.styleSheet.cssText = styles
  } else {
    styleEl.appendChild(document.createTextNode(styles))
  }
  head.appendChild(styleEl)
}

export const addClass = (el, className) => {
  if (el !== null) {
    if (el.classList) {
      el.classList.add(className)
    } else {
      el.className += ` ${className}`
    }
  }
}

export const remove = (el) => {
  if (el !== null && el.parentNode !== null) {
    el.parentNode.removeChild(el)
  }
}

export const removeClass = (el, className) => {
  if (el !== null) {
    if (el.classList) {
      el.classList.remove(className)
    } else {
      el.className = el.className.replace(
        new RegExp(`(^|\\b)${className.split(' ').join('|')}(\\b|$)`, 'gi'),
        ' '
      )
    }
  }
}

/**
 * Returns the height in pixels of a given element from the top of the page.
 *
 * @param element
 */
export function getHeightFromTopOfPage(element: HTMLElement): number {
  let height = element.offsetTop
  let parent = element.parentElement
  while (parent) {
    height += parent.offsetTop
    parent = parent.offsetParent as HTMLElement
  }
  return height
}

/**
 * Returns true if the first element is above the second element
 * @param xElement
 * @param yElement
 */
export function isXAboveY(
  xElement: HTMLElement,
  yElement: HTMLElement
): boolean {
  const xTop = getHeightFromTopOfPage(xElement)
  const yTop = getHeightFromTopOfPage(yElement)

  // Reminder: Lower number means higher on the page!
  return xTop < yTop
}

export const domReady = (fn) => {
  if (document.readyState !== 'loading') {
    fn()
  } else if (document.addEventListener) {
    document.addEventListener('DOMContentLoaded', fn)
  } else {
    document.attachEvent('onreadystatechange', () => {
      if (document.readyState !== 'loading') {
        fn()
      }
    })
  }
}

export const escapeHtml = (str): string => {
  const div = document.createElement('div')
  div.appendChild(document.createTextNode(str))

  return div.innerHTML
}

// UNSAFE with unsafe strings; only use on previously-escaped ones!
export const unescapeHtml = (escapedStr): string => {
  const div = document.createElement('div')
  div.innerHTML = escapedStr
  const child = div.childNodes[0]

  return child && child.nodeValue ? child.nodeValue : ''
}

interface IOnVisibleChangeConfig {
  onVisible: Function
  onHidden: Function
}

/**
 * @param config
 * @returns A function that removes the listeners it created.
 */
export function onVisibilityChange(config: IOnVisibleChangeConfig): () => void {
  let visibilityState
  let visibilityChange

  if (typeof document.hidden !== 'undefined') {
    visibilityChange = 'visibilitychange'
    visibilityState = 'visibilityState'
  } else if (typeof document.mozHidden !== 'undefined') {
    visibilityChange = 'mozvisibilitychange'
    visibilityState = 'mozVisibilityState'
  } else if (typeof document.msHidden !== 'undefined') {
    visibilityChange = 'msvisibilitychange'
    visibilityState = 'msVisibilityState'
  } else if (typeof document.webkitHidden !== 'undefined') {
    visibilityChange = 'webkitvisibilitychange'
    visibilityState = 'webkitVisibilityState'
  }

  function handleVisibilityChange() {
    switch (document[visibilityState]) {
      case 'visible':
        config.onVisible()
        break
      case 'hidden':
        config.onHidden()
        break
      default:
        break
    }
  }

  document.addEventListener(visibilityChange, handleVisibilityChange, false)

  // Return a function that when called, removed the eventListener
  return () => {
    document.removeEventListener(
      visibilityChange,
      handleVisibilityChange,
      false
    )
  }
}

// Returns the content width of the element.
export function getContentWidth(el: HTMLElement): number {
  const styles = window.getComputedStyle(el)

  let contentWidth = parseFloat(styles.width || '0')
  if (styles.boxSizing === 'border-box') {
    contentWidth =
      contentWidth -
      parseFloat(styles.paddingRight || '0') -
      parseFloat(styles.paddingLeft || '0') -
      parseFloat(styles.borderRightWidth || '0') -
      parseFloat(styles.borderLeftWidth || '0')
  }

  return contentWidth
}

export function isBelowViewport({
  selector,
  insertPosition,
  mobileInsertPosition
}: ISlotModel): boolean {
  const viewportHeight = window.innerHeight
  const element =
    selector instanceof Element
      ? selector
      : (document.querySelector(selector) as Element)

  if (!element) {
    return false
  }

  const { top, height } = element.getBoundingClientRect()
  const position = Device.isMobileOrTablet
    ? mobileInsertPosition
    : insertPosition

  const totalDistanceFromViewport =
    position === 'beforebegin' || position === 'afterbegin' ? top : top + height

  return totalDistanceFromViewport > viewportHeight
}

declare global {
  interface HTMLStyleElement {
    styleSheet?: { cssText: string }
  }

  interface Document {
    webkitHidden?: string
    msHidden?: string
    mozHidden?: string
    attachEvent(event: string, listener: EventListener): boolean
    detachEvent(event: string, listener: EventListener): void
  }
}

export const sizeClassPrefix = `mv-size`
export function addAdDimensionsClass(
  event: { size: string | Array<number> },
  element: HTMLElement
): void {
  removeWildcardClass(sizeClassPrefix, element)
  const klass = `${sizeClassPrefix}-${event.size[0]}x${event.size[1]}`
  element.classList.add(klass)
}

export function removeWildcardClass(
  partialKlass: string,
  element: HTMLElement
): void {
  Array.from(element.classList).forEach((klass) => {
    if (klass.match(new RegExp(partialKlass, 'g'))) {
      element.classList.remove(klass)
    }
  })
}
